/**
 * Decisão pura do payload de grupos de um contact point no modo "grupos".
 *
 * Regra (contrato do backend: lista presente substitui os grupos, lista ausente mantém):
 * - na edição, NÃO enviar a lista quando a seleção não mudou (preserva vínculos);
 * - na edição, NÃO enviar a lista quando a busca de grupos falhou (evita apagar sem dados);
 * - caso contrário, enviar os uuids da seleção atual (nomes sem uuid conhecido são descartados).
 */
export interface GroupPayloadInput {
    forUpdate: boolean;
    groupsLoadFailed: boolean;
    selectedGroups: string[];
    initialGroups: string[];
    groupUuidByName: Record<string, string>;
}

export const groupTemplatePayload = (
    input: GroupPayloadInput,
): { templateGroupUuids: string[] } | Record<string, never> => {
    if (input.forUpdate) {
        if (input.groupsLoadFailed) return {};
        const sortedSelection = [...input.selectedGroups].sort();
        const sortedInitial = [...input.initialGroups].sort();
        if (JSON.stringify(sortedSelection) === JSON.stringify(sortedInitial)) return {};
    }
    const uuids = input.selectedGroups
        .map((name) => input.groupUuidByName[name])
        .filter(Boolean);
    return { templateGroupUuids: uuids };
};

export interface CompositionRef {
    id: string;
    name: string;
}

export interface CustomPayloadInput {
    forUpdate: boolean;
    selectedTemplates: string[];
    initialTemplates: string[];
    initialGroups: string[];
    compositions: CompositionRef[];
}

export const customTemplatePayload = (
    input: CustomPayloadInput,
): { templates: string[]; templateGroupUuids: string[] } | Record<string, never> => {
    const unchanged = JSON.stringify(input.selectedTemplates) === JSON.stringify(input.initialTemplates);
    if (input.forUpdate && unchanged && input.initialGroups.length === 0) return {};
    const idByName = new Map(input.compositions.map((c) => [c.name, c.id]));
    return {
        templates: input.selectedTemplates.map((entry) => idByName.get(entry) ?? entry),
        templateGroupUuids: [],
    };
};

export const groupsDroppedByCustomMode = (input: {
    customMode: boolean;
    forUpdate: boolean;
    selectedGroups: string[];
    initialGroups: string[];
}): string[] => {
    if (!input.customMode) return [];
    return input.forUpdate ? input.initialGroups : input.selectedGroups;
};

export const toggleComposition = (selected: string[], composition: CompositionRef): string[] => {
    const isSelected = selected.includes(composition.id) || selected.includes(composition.name);
    if (isSelected) return selected.filter((entry) => entry !== composition.id && entry !== composition.name);
    return [...selected, composition.id];
};
